/**
 * i18next configuration for Social Pulse.
 *
 * Language resolution order:
 *   1. `sp:pref-lang` in localStorage (set by <LanguageChip />)
 *   2. `preferred_language` on the signed-in user record (server-synced by LanguageChip)
 *   3. Browser language detector
 *   4. English
 *
 * All namespaces are baked into the bundle (no HTTP loader) so:
 *   • The chrome renders instantly with no lang-switch flicker
 *   • Add-a-language is as simple as dropping a new JSON block below
 *
 * Adding new strings:
 *   • Reserve the key in `en.common` first — English is the source of truth.
 *   • Add translations in each language block that already exists.
 *   • Missing keys silently fall back to English (react-i18next default).
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en";
import es from "./locales/es";
import fr from "./locales/fr";
import de from "./locales/de";
import pt from "./locales/pt";
import it from "./locales/it";
import ru from "./locales/ru";
import uk from "./locales/uk";
import hi from "./locales/hi";
import mr from "./locales/mr";
import bn from "./locales/bn";
import ta from "./locales/ta";
import te from "./locales/te";
import ml from "./locales/ml";
import kn from "./locales/kn";
import gu from "./locales/gu";
import pa from "./locales/pa";
import ur from "./locales/ur";
import ar from "./locales/ar";
import fa from "./locales/fa";
import he from "./locales/he";
import zh from "./locales/zh";
import ja from "./locales/ja";
import ko from "./locales/ko";
import tr from "./locales/tr";
import vi from "./locales/vi";
import id from "./locales/id";
import th from "./locales/th";
import sw from "./locales/sw";

const resources = {
  en, es, fr, de, pt, it, ru, uk,
  hi, mr, bn, ta, te, ml, kn, gu, pa, ur,
  ar, fa, he, zh, ja, ko, tr, vi, id, th, sw,
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: Object.keys(resources),
    ns: ["common"],
    defaultNS: "common",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "sp:pref-lang",
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  });

// RTL languages need `dir="rtl"` on <html>. Sync it whenever i18n changes.
const RTL_LANGS = new Set(["ar", "fa", "he", "ur"]);
function syncDir(lng) {
  try {
    const short = (lng || "en").toLowerCase().split("-")[0];
    document.documentElement.setAttribute("lang", short);
    document.documentElement.setAttribute("dir", RTL_LANGS.has(short) ? "rtl" : "ltr");
  } catch { /* ssr / private-mode */ }
}
syncDir(i18n.resolvedLanguage);
i18n.on("languageChanged", syncDir);

export default i18n;
